import React, { createContext, useState, useContext, useEffect } from "react";

// Create context
const TranslationContext = createContext();

// Available languages configuration
const AVAILABLE_LANGUAGES = {
  en: { name: "English", code: "en" },
  fr: { name: "Français", code: "fr" },
  es: { name: "Español", code: "es" },
  vi: { name: "Tiếng Việt", code: "vi" },
  it: { name: "Italiano", code: "it" },
  tr: { name: "Türkçe", code: "tr" }
};

// Default language
const DEFAULT_LANGUAGE = "en";

// Storage key for persisting language preference
const LANGUAGE_STORAGE_KEY = "fiq_lng";

// Language detection and initialization utility
const detectInitialLanguage = () => {
  try {
    // 1. Check localStorage for saved preference
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage && AVAILABLE_LANGUAGES[savedLanguage]) {
      console.log('Using saved language preference:', savedLanguage);
      return savedLanguage;
    }

    // 2. Check browser language preferences
    const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage];
    console.log('Browser languages:', browserLanguages);
    
    for (const browserLang of browserLanguages) {
      // Extract language code (e.g., 'en-US' -> 'en')
      const langCode = browserLang.split('-')[0].toLowerCase();
      if (AVAILABLE_LANGUAGES[langCode]) {
        console.log('Using browser language:', langCode);
        return langCode;
      }
    }

    // 3. Fallback to default language
    console.log('Using default language:', DEFAULT_LANGUAGE);
    return DEFAULT_LANGUAGE;
  } catch (error) {
    console.error('Error detecting initial language:', error);
    return DEFAULT_LANGUAGE;
  }
};

// Provider component
export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => detectInitialLanguage());
  const [translations, setTranslations] = useState({});
  const [isTranslating, setIsTranslating] = useState(true);
  const [languageLoadError, setLanguageLoadError] = useState(null);

  // Load translations dynamically from the Languages folder
  useEffect(() => {
    const loadTranslations = async () => {
      setIsTranslating(true);
      setLanguageLoadError(null);
      
      try {
        // Get base path for language files
        const getLanguagePath = () => {
          // Check for runtime configuration first
          if (typeof window !== 'undefined' && window.RUNTIME_CONFIG?.deployment?.basePath) {
            const basePath = window.RUNTIME_CONFIG.deployment.basePath;
            return basePath.endsWith('/') ? `${basePath}Languages/` : `${basePath}/Languages/`;
          }
          
          // Check for global config
          if (global.config?.deployment?.basePath) {
            const basePath = global.config.deployment.basePath;
            return basePath.endsWith('/') ? `${basePath}Languages/` : `${basePath}/Languages/`;
          }
          
          // Check for app basePath
          if (global.config?.app?.basePath) {
            const basePath = global.config.app.basePath;
            return basePath.endsWith('/') ? `${basePath}Languages/` : `${basePath}/Languages/`;
          }
          
          // Check PUBLIC_URL environment variable
          if (process.env.PUBLIC_URL) {
            const basePath = process.env.PUBLIC_URL;
            return basePath.endsWith('/') ? `${basePath}Languages/` : `${basePath}/Languages/`;
          }
          
          // Default to relative path for portable deployment
          return './Languages/';
        };
        
        const languagePath = getLanguagePath();
        console.log('Loading language files from:', languagePath);
        
        // Load all available languages
        const languagePromises = Object.keys(AVAILABLE_LANGUAGES).map(async (langCode) => {
          try {
            const response = await fetch(`${languagePath}${langCode}.json`);
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            return { langCode, data };
          } catch (error) {
            console.error(`Failed to load ${langCode}.json:`, error);
            return { langCode, data: {} }; // Return empty object as fallback
          }
        });

        const results = await Promise.all(languagePromises);
        
        // Build translations object
        const loadedTranslations = {};
        results.forEach(({ langCode, data }) => {
          loadedTranslations[langCode] = { default: data };
        });
        
        setTranslations(loadedTranslations);
        console.log('Translations loaded successfully:', Object.keys(loadedTranslations));
        
      } catch (error) {
        console.error("Critical error loading translations:", error);
        setLanguageLoadError(error.message);
        
        // Fallback to empty translations for all languages
        const fallbackTranslations = {};
        Object.keys(AVAILABLE_LANGUAGES).forEach(langCode => {
          fallbackTranslations[langCode] = { default: {} };
        });
        setTranslations(fallbackTranslations);
      } finally {
        setIsTranslating(false);
      }
    };

    loadTranslations();
  }, []);

  // Function to change language and persist the choice
  const changeLanguage = React.useCallback((langCode) => {
    console.log('=== changeLanguage called ===');
    console.log('Requested language:', langCode);
    console.log('Available languages:', Object.keys(AVAILABLE_LANGUAGES));
    console.log('Current language before change:', currentLanguage);
    
    if (AVAILABLE_LANGUAGES[langCode]) {
      console.log('Changing language to:', langCode);
      setCurrentLanguage(langCode);
      
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, langCode);
        console.log('Language preference saved to localStorage:', langCode);
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    } else {
      console.error('Unsupported language code:', langCode);
    }
    console.log('=== changeLanguage completed ===');
  }, [currentLanguage]); // Include currentLanguage as dependency for debugging purposes

  // Get the current language translations - ensure this creates a new reference when language changes
  const language = React.useMemo(() => {
    
    const langData = translations[currentLanguage]?.default || {};
    console.log(`=== Language data update ===`);
    console.log(`Current language: ${currentLanguage}`);
    console.log(`Available translations:`, Object.keys(translations));
    console.log(`Language data for ${currentLanguage}:`, Object.keys(langData));
    console.log(`Sample translations:`, {
      loginTitle: langData.loginTitle,
      navigation: langData.navigation,
      gallery: langData.gallery
    });
    console.log(`=== End language data update ===`);
    return langData;
  }, [translations, currentLanguage]);

  // Context value - memoize to prevent unnecessary re-renders but ensure updates when dependencies change
  const contextValue = React.useMemo(() => ({
    language,
    currentLanguage,
    availableLanguages: AVAILABLE_LANGUAGES,
    changeLanguage,
    setCurrentLanguage: changeLanguage, // Backward compatibility
    isTranslating,
    languageLoadError
  }), [language, currentLanguage, changeLanguage, isTranslating, languageLoadError]);

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook to use the translation context
export const useTranslation = () => {
  const context = useContext(TranslationContext);

  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};

// Helper function to get available languages (can be imported anywhere)
export const getAvailableLanguages = () => AVAILABLE_LANGUAGES;

// Helper function to get current language from storage (can be imported anywhere)
export const getCurrentLanguageFromStorage = () => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE;
  } catch (error) {
    console.error('Error reading language from storage:', error);
    return DEFAULT_LANGUAGE;
  }
};

// Helper function to set language in storage (can be imported anywhere)
export const setLanguageInStorage = (langCode) => {
  try {
    if (AVAILABLE_LANGUAGES[langCode]) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, langCode);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error saving language to storage:', error);
    return false;
  }
};